import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';

// Public routes — loaded synchronously (critical path for unauthenticated users)
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import PublicCollectionPage from './components/collection/PublicCollectionPage';
import PublicWordPage from './components/word/PublicWordPage';
import PublicLessonPage from './components/lesson/PublicLessonPage';
import AuthCallback from './components/AuthCallback';
import EmailVerificationPage from './components/EmailVerificationPage';
import EmailPreferencesPage from './components/EmailPreferencesPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import DeleteAccount from './components/DeleteAccount';
import AccountDeletionConfirmPage from './components/AccountDeletionConfirmPage';
import { useAuth } from './context/AuthContext';
import { useTheme, parseThemePreference } from './context/ThemeContext';
import { StatsRefreshProvider } from './context/StatsRefreshContext';
import { TourProvider } from './context/TourContext';
import TourOverlay from './components/tour/TourOverlay';
import Navigation from './components/Navigation';
import AppLayout from './components/AppLayout';
import LoginRedirect from './components/common/LoginRedirect';
import { ConsentProvider } from './context/ConsentContext';
import SignedInHint from './components/common/SignedInHint';
import { FeatureRoute } from './context/FeatureFlagsContext';
import { FF_LESSON_MODE } from './config/featureFlags';
import CookieBanner from './components/consent/CookieBanner';
import FixLanguagesPrompt, { showsLanguageFixPrompt } from './components/common/FixLanguagesPrompt';
import { leaveSsrPage } from './utils/ssrPage';
import './styles/app.css';

// Authenticated routes — lazy-loaded (only needed after login)
const MainPage = lazy(() => import('./components/MainPage'));
const VocabularyList = lazy(() => import('./components/VocabularyList'));
const AddVocabulary = lazy(() => import('./components/AddVocabulary'));
const ProfileSetup = lazy(() => import('./components/ProfileSetup'));
const AddVocabularyNew = lazy(() => import('./components/AddVocabularyNew'));
const UserTags = lazy(() => import('./components/UserTags'));
const CollectionsPage = lazy(() => import('./components/collection/CollectionPage'));
const AddCollection = lazy(() => import('./components/collection/AddCollection'));
const Practice = lazy(() => import('./components/practice/Practice'));
const Statistics = lazy(() => import('./components/statistics/Statistics'));
const ProfileSettings = lazy(() => import('./components/profile/ProfileSettings'));
const AddWordPage = lazy(() => import('./components/word/addWord'));
const CollectionList = lazy(() => import('./components/collection/CollectionList'));
const EditCollection = lazy(() => import('./components/collection/EditCollection'));
const ImportCollection = lazy(() => import('./components/collection/ImportCollection'));
const EditWordPage = lazy(() => import('./components/word/editWord'));
const PronunciationPage = lazy(() => import('./components/word/PronunciationPage'));
const ImageAssociationPage = lazy(() => import('./components/word/ImageAssociationPage'));
const SpacedRepetitionFlascards = lazy(() => import('./components/spaced-repetition/SpacedRepetitionFlashcards'));
const SpacedRepetitionSelector = lazy(() => import('./components/spaced-repetition/SpacedRepetitionSelector'));
const CreateLesson = lazy(() => import('./components/lesson/CreateLesson'));
const LessonList = lazy(() => import('./components/lesson/LessonList'));
const LessonPage = lazy(() => import('./components/lesson/LessonPage'));
const SpacedRepetitionQuiz = lazy(() => import('./components/spaced-repetition/SpacedRepetitionQuiz'));
const ErrorReview = lazy(() => import('./components/spaced-repetition/ErrorReview'));
const SubscriptionSuccess = lazy(() => import('./components/subscription/SubscriptionSuccess'));
const SubscriptionCancel = lazy(() => import('./components/subscription/SubscriptionCancel'));
// Not wrapped in PrivateRoute: it handles signed-out users itself (remembering its URL so sign-in
// returns to it), and must not be detoured to onboarding mid-flow.
const OAuthConsent = lazy(() => import('./components/connect/OAuthConsent'));



const CHUNK_RELOAD_FLAG = 'chunkErrorReloaded';

class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode; resetKey: string },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch() {
    // React.lazy() caches the rejected import() promise on the lazy component itself, so simply
    // clearing hasError on navigation (below) does NOT retry the chunk that actually failed to
    // load — it only lets OTHER routes work again. The common real-world cause is a stale chunk
    // hash after a deploy, which a single hard reload fixes (fresh index.html -> fresh hashes).
    // Guard with a sessionStorage flag so a genuinely broken chunk doesn't reload-loop forever.
    if (!sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      window.location.reload();
    }
  }
  componentDidUpdate(prevProps: { resetKey: string }) {
    // Recovers routes other than the one that failed — see componentDidCatch for why the failed
    // route itself needs a reload, not just this state reset, to actually retry.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Failed to load page.{' '}
            <button onClick={() => window.location.reload()}>Reload</button>
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authContext = useAuth();
  const location = useLocation();
  const user = authContext ? authContext.user : null;
  const isLoading = authContext ? authContext.isLoading : true;
  if (isLoading) return null;
  if (!user) return <LoginRedirect />;
  // A user who skipped onboarding (e.g. signed up just to approve an AI app, which returns
  // them to the app instead of /profile-setup) finishes it on their next visit.
  if (authContext?.userSettings?.onboarding_complete === false && location.pathname !== '/profile-setup') {
    return <Navigate to="/profile-setup" replace />;
  }
  return <>{children}</>;
};

const LayoutRoute: React.FC<{ children: React.ReactNode; hideSidebarOnMobile?: boolean }> = ({ children, hideSidebarOnMobile }) => (
  <PrivateRoute>
    <AppLayout hideSidebarOnMobile={hideSidebarOnMobile}>{children}</AppLayout>
  </PrivateRoute>
);
function App() {
  const authContext = useAuth();
  const user = authContext ? authContext.user : null;
  const isLoading = authContext ? authContext.isLoading : true;
  const location = useLocation();

  const { setTheme } = useTheme();
  const initialThemeSynced = useRef(false);

  // A server-rendered share page's embedded data must not outlive it, and later pages get the
  // shell's generic head tags back — see utils/ssrPage.ts.
  useEffect(() => { leaveSsrPage(location.pathname); }, [location.pathname]);

  useEffect(() => {
    if (!user || initialThemeSynced.current) return;

    const rawTheme = authContext?.userSettings?.display_settings?.theme;
    if (rawTheme) {
      setTheme(parseThemePreference(rawTheme));
      initialThemeSynced.current = true;
    }
  }, [user, authContext?.userSettings?.display_settings?.theme, setTheme]);

  return (
    <ConsentProvider>
      <SignedInHint />
      <TourProvider>
        <StatsRefreshProvider>
          <div className="App">
            {user && location.pathname !== '/profile-setup' && <nav>
              <ul>
                <Navigation />
              </ul>
            </nav>}

            <ChunkErrorBoundary resetKey={location.pathname}>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={isLoading ? null : user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/verify-email" element={<EmailVerificationPage />} />
                <Route path="/email-preferences" element={<EmailPreferencesPage />} />
                <Route path="/c/:token" element={<PublicCollectionPage />} />
                <Route path="/w/:token" element={<PublicWordPage />} />
                <Route path="/l/:token" element={<PublicLessonPage />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/delete-account" element={<DeleteAccount />} />
                <Route path="/account-deletion/confirm" element={<AccountDeletionConfirmPage />} />
                <Route path="/profile-setup" element={
                  <PrivateRoute>
                    <ProfileSetup />
                  </PrivateRoute>
                } />
                <Route path="/dashboard" element={<LayoutRoute><MainPage /></LayoutRoute>} />
                <Route path="/collections" element={<LayoutRoute><CollectionsPage /></LayoutRoute>} />
                <Route path="/AddCollection" element={<LayoutRoute><AddCollection /></LayoutRoute>} />
                <Route path="/EditCollection" element={<LayoutRoute><EditCollection /></LayoutRoute>} />
                <Route path="/ImportCollection" element={<LayoutRoute><ImportCollection /></LayoutRoute>} />
                <Route path="/practice" element={<LayoutRoute><Practice /></LayoutRoute>} />
                <Route path="/statistics" element={<LayoutRoute hideSidebarOnMobile><Statistics /></LayoutRoute>} />
                <Route path="/add" element={<LayoutRoute><AddVocabulary /></LayoutRoute>} />
                <Route path="/vocabularyList" element={<LayoutRoute><VocabularyList /></LayoutRoute>} />
                <Route path="/collectionList" element={<LayoutRoute><CollectionList /></LayoutRoute>} />
                <Route path="/addnew" element={<LayoutRoute><AddVocabularyNew /></LayoutRoute>} />
                <Route path="/addWord" element={<LayoutRoute><AddWordPage /></LayoutRoute>} />
                <Route path="/editWord" element={<LayoutRoute><EditWordPage /></LayoutRoute>} />
                <Route path="/pronunciation" element={<LayoutRoute><PronunciationPage /></LayoutRoute>} />
                <Route path="/image-association" element={<LayoutRoute><ImageAssociationPage /></LayoutRoute>} />
                <Route path="/user" element={<LayoutRoute hideSidebarOnMobile><UserTags /></LayoutRoute>} />
                <Route path="/profileSettings" element={<LayoutRoute><ProfileSettings /></LayoutRoute>} />
                <Route path="/spaced-repetition" element={<LayoutRoute hideSidebarOnMobile><SpacedRepetitionSelector /></LayoutRoute>} />
                <Route path="/spaced-repetition/flashcard" element={<LayoutRoute hideSidebarOnMobile><SpacedRepetitionFlascards /></LayoutRoute>} />
                <Route path="/spaced-repetition/quiz" element={<LayoutRoute hideSidebarOnMobile><SpacedRepetitionQuiz /></LayoutRoute>} />
                <Route path="/error-review" element={<LayoutRoute hideSidebarOnMobile><ErrorReview /></LayoutRoute>} />
                <Route path="/lessons" element={<LayoutRoute><FeatureRoute flag={FF_LESSON_MODE}><LessonList /></FeatureRoute></LayoutRoute>} />
                <Route path="/lessons/new" element={<LayoutRoute><FeatureRoute flag={FF_LESSON_MODE}><CreateLesson /></FeatureRoute></LayoutRoute>} />
                <Route path="/lessons/:id" element={<LayoutRoute><FeatureRoute flag={FF_LESSON_MODE}><LessonPage /></FeatureRoute></LayoutRoute>} />
                <Route path="/subscription/success" element={<PrivateRoute><SubscriptionSuccess /></PrivateRoute>} />
                <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
                <Route path="/oauth/consent" element={<OAuthConsent />} />
              </Routes>
            </Suspense>
            </ChunkErrorBoundary>
            {user && showsLanguageFixPrompt(location.pathname) && <FixLanguagesPrompt />}
            <CookieBanner />
          </div>
          <TourOverlay />
        </StatsRefreshProvider>
      </TourProvider>
    </ConsentProvider>
  );
}

export default App;